<template>
  <div>
    <!-- Header info -->
    <div class="px-6 pt-4 pb-2 border-b mb-4">
      <h3 class="text-base font-semibold text-gray-700">Upload Lab Results</h3>
      <p class="text-sm text-gray-500 mt-1">
        Enter results for lab tests conducted at an external facility. All results will be captured and published in one step.
      </p>
    </div>

    <!-- Add External Test — for a test that was never requested through this system at all -->
    <div class="px-6 pb-4 border-b mb-4">
      <button
        v-if="!showAddTest"
        type="button"
        @click="showAddTest = true"
        class="px-4 py-1.5 border border-dashed border-custom-yellow text-custom-blue rounded hover:bg-yellow-50 text-sm font-medium"
      >
        + Add External Test
      </button>
      <div v-else class="border rounded-lg p-4 bg-gray-50">
        <p class="text-sm text-gray-600 mb-3">
          Log a lab test the patient already had done externally — no prior request needed.
        </p>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">
              <span class="text-red-500">* </span>Test Type
            </label>
            <div class="search-wrapper">
              <v-select
                v-model="newTest.testType"
                :options="testTypeOptions"
                :filterable="false"
                placeholder="Search for lab tests"
                @search="onTestTypeSearch"
                label="name"
                :loading="isSearchingTestTypes"
              >
                <template #no-options="{ searching, loading }">
                  <template v-if="searching">No results found for "{{ searching }}"</template>
                  <template v-else-if="loading">Loading results...</template>
                  <template v-else>Type to search for lab tests</template>
                </template>
              </v-select>
            </div>
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input
              type="text"
              v-model="newTest.notes"
              placeholder="Optional"
              class="border border-gray-300 rounded p-2 bg-white text-sm"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-3">
          <button type="button" @click="cancelAddTest" :disabled="addingTest"
            class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-medium disabled:opacity-50">
            Cancel
          </button>
          <button type="button" @click="submitAddExternalTest" :disabled="!newTest.testType || addingTest"
            class="px-4 py-1.5 bg-custom-yellow text-white rounded hover:bg-custom-blue text-sm font-medium disabled:opacity-50">
            {{ addingTest ? "Adding..." : "Add Test" }}
          </button>
        </div>
      </div>
    </div>

    <!-- No lab tests -->
    <div v-if="pendingTests.length === 0" class="text-center text-green-600 font-medium mt-8 px-6">
      <p>No referred-out lab tests found for this visit.</p>
      <p class="text-sm text-gray-500 mt-1">Any in-house lab tests will be handled through the laboratory queue.</p>
    </div>

    <div v-else class="px-6 pb-6 max-h-[58vh] overflow-y-auto">
      <!-- Per-test result entry -->
      <div
        v-for="(test, index) in pendingTests"
        :key="test.id"
        class="border rounded-lg p-4 mb-4 bg-gray-50"
      >
        <!-- Test card header -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="font-semibold text-custom-blue">
            {{ index + 1 }}. {{ test.test_type?.name }}
          </h4>
          <span
            class="text-xs px-2 py-1 rounded-full"
            :class="getStatusClass(test.status?.code)"
          >
            {{ test.status?.name }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <!-- Structured analyte entry — same table used in the Laboratory interface's Conduct Test tab -->
          <div v-if="(analytes[test.id] || []).length > 0" class="col-span-2">
            <label class="text-sm font-medium text-gray-700 mb-2 block">
              <span class="text-red-500">* </span>Test Results
            </label>
            <div v-if="analytesLoading[test.id]" class="text-sm text-gray-400 italic">Loading analytes...</div>
            <table v-else class="w-full text-sm border border-gray-200 rounded">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Analyte</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Result</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Unit</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Reference Range</th>
                  <th class="px-3 py-2 text-left font-medium text-gray-600">Flag</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="analyte in analytes[test.id]" :key="analyte.id">
                  <td class="px-3 py-2 font-medium">
                    {{ analyte.name }}
                    <span v-if="analyte.abbreviation" class="text-xs text-gray-400 ml-1">({{ analyte.abbreviation }})</span>
                  </td>
                  <td class="px-3 py-2">
                    <input
                      v-if="analyte.result_type === 'numeric'"
                      type="number"
                      step="any"
                      v-model="forms[test.id].analyte_results[analyte.id]"
                      class="w-28 border border-gray-300 rounded px-2 py-1 bg-gray-50 focus:ring-1 focus:ring-blue-400"
                      placeholder="0.0"
                    />
                    <select
                      v-else-if="analyte.result_type === 'select'"
                      v-model="forms[test.id].analyte_results[analyte.id]"
                      class="w-40 border border-gray-300 rounded px-2 py-1 bg-gray-50"
                    >
                      <option value="" disabled>Select...</option>
                      <option v-for="opt in analyte.select_options" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                    <input
                      v-else
                      type="text"
                      v-model="forms[test.id].analyte_results[analyte.id]"
                      class="w-40 border border-gray-300 rounded px-2 py-1 bg-gray-50"
                      placeholder="Enter value"
                    />
                  </td>
                  <td class="px-3 py-2 text-gray-500">{{ analyte.unit || '—' }}</td>
                  <td class="px-3 py-2 text-gray-500 text-xs">
                    <span v-if="analyte.has_reference_range && analyte.ref_range_adult_male_low != null">
                      {{ analyte.ref_range_adult_male_low }} – {{ analyte.ref_range_adult_male_high }}
                    </span>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-2">
                    <span
                      v-if="liveFlag(analyte, forms[test.id].analyte_results[analyte.id]) === 'H'"
                      class="px-2 py-0.5 text-xs font-bold rounded bg-red-100 text-red-700"
                    >H</span>
                    <span
                      v-else-if="liveFlag(analyte, forms[test.id].analyte_results[analyte.id]) === 'L'"
                      class="px-2 py-0.5 text-xs font-bold rounded bg-blue-100 text-blue-700"
                    >L</span>
                    <span
                      v-else-if="liveFlag(analyte, forms[test.id].analyte_results[analyte.id]) === 'N'"
                      class="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-700"
                    >N</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Fallback free-text result — only for tests with no linked analytes -->
          <div v-else class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">
              <span class="text-red-500">* </span>Test Results
            </label>
            <input
              type="text"
              v-model="forms[test.id].results"
              placeholder="Enter results..."
              class="border border-gray-300 rounded p-2 bg-white text-sm"
            />
          </div>

          <!-- Lab Comments — textarea -->
          <div class="flex flex-col" :class="{ 'col-span-2': (analytes[test.id] || []).length > 0 }">
            <label class="text-sm font-medium text-gray-700 mb-1">Lab Comments</label>
            <textarea
              v-model="forms[test.id].comment"
              rows="4"
              placeholder="Add comments..."
              class="border border-gray-300 rounded p-2 bg-white text-sm resize-none"
            />
          </div>

          <!-- File Upload — spans both columns -->
          <div class="flex flex-col col-span-2">
            <label class="text-sm font-medium text-gray-700 mb-1">
              Upload Documents
              <span class="text-xs text-gray-500 font-normal ml-1">(Max 5 files, 10MB each — JPG, PNG, PDF)</span>
            </label>
            <label
              :for="`fileInput-${test.id}`"
              class="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-400 rounded bg-white mt-1 cursor-pointer hover:bg-gray-100 transition"
            >
              <input
                :id="`fileInput-${test.id}`"
                type="file"
                class="hidden"
                @change="handleFileSelect($event, test.id)"
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
              />
              <img :src="uploadFileIcon" alt="upload" class="w-5 h-5" />
              <span class="text-sm text-blue-600">
                {{ (selectedFiles[test.id] || []).length === 0
                    ? 'Add Lab Documents'
                    : `${selectedFiles[test.id].length} file(s) selected` }}
              </span>
            </label>

            <!-- File previews -->
            <div v-if="(selectedFiles[test.id] || []).length > 0" class="flex flex-wrap mt-2 gap-2">
              <div
                v-for="(fileObj, fileIndex) in selectedFiles[test.id]"
                :key="fileIndex"
                class="flex flex-col items-center gap-1 p-2 bg-white border rounded max-w-[100px]"
              >
                <span class="text-xs text-gray-600 text-center truncate w-full" :title="fileObj.file.name">{{ fileObj.file.name }}</span>

                <img
                  v-if="fileObj.file.type.startsWith('image/')"
                  :src="fileObj.preview"
                  class="w-12 h-12 object-cover rounded"
                />
                <div
                  v-else-if="fileObj.file.type === 'application/pdf'"
                  class="w-12 h-12 bg-red-50 border border-red-200 rounded flex items-center justify-center"
                >
                  <span class="text-xs font-bold text-red-600">PDF</span>
                </div>

                <!-- Upload progress bar -->
                <div v-if="(uploadProgress[test.id] || [])[fileIndex]" class="w-20">
                  <div class="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      class="bg-custom-yellow h-1.5 rounded-full transition-all"
                      :style="{ width: (uploadProgress[test.id][fileIndex]) + '%' }"
                    />
                  </div>
                </div>

                <button
                  @click="removeFile(test.id, fileIndex)"
                  class="text-red-500 hover:text-red-700"
                  type="button"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Captured badge -->
        <div v-if="submittedIds.includes(test.id)" class="mt-3 text-green-600 text-xs font-medium">
          ✓ Results captured
        </div>
      </div>

      <!-- Review comment + publish -->
      <div class="mt-6 border-t pt-4">
        <label class="text-sm font-semibold text-gray-700 block mb-1">
          Review Comments
          <span class="text-gray-400 font-normal">(required before publishing)</span>
        </label>
        <textarea
          v-model="reviewComment"
          rows="3"
          placeholder="Enter your review comments..."
          class="w-full border border-gray-300 rounded p-2 bg-white text-sm"
        />

        <div class="flex justify-end mt-4">
          <span v-if="allPublished" class="text-green-600 font-semibold text-sm">
            ✓ All results captured and published
          </span>
          <button
            v-else
            :disabled="saving"
            @click="captureAndPublish"
            class="px-6 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600 disabled:opacity-50 text-sm font-medium"
          >
            {{ saving ? "Saving..." : "Capture & Publish Results" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import AWS from "aws-sdk";
import uploadFileIcon from "@/assets/icons/upload.png";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "ExternalLabResultsTab",
  components: { vSelect },

  props: {
    labTests: {
      type: Array,
      default: () => [],
    },
    visitId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      forms: {},           // keyed by test.id → { results, comment, analyte_results: { [analyteId]: value } }
      selectedFiles: {},   // keyed by test.id → [{ file, preview }]
      uploadProgress: {},  // keyed by test.id → [0-100, ...]
      submittedIds: [],    // test IDs successfully captured in this session
      reviewComment: "",
      saving: false,
      allPublished: false,
      uploadFileIcon,
      // ── Structured analyte results (same source as the Lab interface's Conduct Test tab) ──
      analytes: {},        // keyed by test.id → [LabAnalyte, ...]
      analytesLoading: {}, // keyed by test.id → Boolean
      // ── Add External Test ──────────────────────────────────────────────
      showAddTest: false,
      newTest: { testType: null, notes: "" },
      testTypeOptions: [],
      isSearchingTestTypes: false,
      addingTest: false,
      testTypeSearchTimeout: null,
    };
  },

  computed: {
    // Show only referred-out tests that are NOT yet published (ST-013).
    // In-house tests (referred_out: false) are handled entirely by the lab queue
    // and should never appear here.
    pendingTests() {
      return (this.labTests || []).filter(
        t => t.referred_out === true && t.status?.code !== "ST-013"
      );
    },

    currentUser() {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    },
  },

  watch: {
    labTests: {
      immediate: true,
      handler(tests) {
        if (!tests?.length) return;

        // Only initialise form state for referred-out tests — in-house tests
        // are never shown in this tab and don't need state allocated.
        tests.forEach(t => {
          if (t.referred_out !== true) return;
          if (!this.forms[t.id]) {
            this.forms[t.id] = { results: "", comment: "", analyte_results: {} };
          }
          if (!this.selectedFiles[t.id]) {
            this.selectedFiles[t.id]  = [];
            this.uploadProgress[t.id] = [];
          }
          if (!this.analytes[t.id] && !this.analytesLoading[t.id]) {
            this.loadAnalytesForTest(t);
          }
        });

        // allPublished is true when every referred-out test is already published
        const referredOut = tests.filter(t => t.referred_out === true);
        this.allPublished = referredOut.length > 0 && referredOut.every(t => t.status?.code === "ST-013");
      },
    },
  },

  methods: {
    getStatusClass(code) {
      switch (code) {
        case "ST-006": return "bg-blue-100 text-blue-700";     // Pending
        case "ST-011": return "bg-yellow-100 text-yellow-700"; // Collected
        case "ST-003": return "bg-green-100 text-green-700";   // Completed
        case "ST-013": return "bg-purple-100 text-purple-700"; // Published
        default:       return "bg-gray-100 text-gray-700";
      }
    },

    // ── Structured analyte results ────────────────────────────────────────────

    async loadAnalytesForTest(test) {
      if (!test.test_type?.id) return;
      this.analytesLoading = { ...this.analytesLoading, [test.id]: true };
      try {
        const res = await apiClient.fetchAnalytesForService(test.test_type.id);
        this.analytes = { ...this.analytes, [test.id]: res.success ? (res.data || []) : [] };
      } catch {
        this.analytes = { ...this.analytes, [test.id]: [] };
      } finally {
        this.analytesLoading = { ...this.analytesLoading, [test.id]: false };
      }
    },

    liveFlag(analyte, value) {
      if (analyte.result_type !== "numeric" || !analyte.has_reference_range) return null;
      const v = parseFloat(value);
      if (isNaN(v) || value === "") return null;
      const low  = parseFloat(analyte.ref_range_adult_male_low);
      const high = parseFloat(analyte.ref_range_adult_male_high);
      if (!isNaN(low) && v < low)  return "L";
      if (!isNaN(high) && v > high) return "H";
      return "N";
    },

    // ── Add External Test ─────────────────────────────────────────────────────

    onTestTypeSearch(search, loading) {
      if (search.length < 3) return;
      loading(true);
      if (this.testTypeSearchTimeout) clearTimeout(this.testTypeSearchTimeout);
      this.isSearchingTestTypes = true;
      this.testTypeSearchTimeout = setTimeout(async () => {
        try {
          const response = await apiClient.searchConsultationFacilityServices({ name: search, type: "FST-002" });
          if (response.success) this.testTypeOptions = response.data;
        } catch {
          this.$toast.error("Unable to search lab tests.");
        } finally {
          this.isSearchingTestTypes = false;
          loading(false);
        }
      }, 300);
    },

    cancelAddTest() {
      this.showAddTest = false;
      this.newTest = { testType: null, notes: "" };
    },

    async submitAddExternalTest() {
      if (!this.newTest.testType) return;
      this.addingTest = true;
      try {
        const response = await apiClient.addExternalLabTest({
          visit_id:    this.visitId,
          test_type_id: this.newTest.testType.id,
          notes:       this.newTest.notes,
          userId:      this.currentUser.UserId,
          facilityId:  this.currentUser.FacilityId,
        });
        if (response.success) {
          this.$toast.success("External lab test added.");
          this.cancelAddTest();
          this.$emit("results-published"); // reuses the parent's existing refetch to pick up the new test
        } else {
          this.$toast.error(response.message || "Failed to add external test.");
        }
      } catch (error) {
        this.$toast.error("Failed to add external test: " + error.message);
      } finally {
        this.addingTest = false;
      }
    },

    // ── File handling ──────────────────────────────────────────────────────────

    handleFileSelect(event, testId) {
      const files = Array.from(event.target.files);
      this.addFiles(files, testId);
      // Reset so the same file can be re-selected after being removed
      event.target.value = "";
    },

    addFiles(files, testId) {
      const valid = files.filter(
        f => ["image/jpeg", "image/png", "application/pdf"].includes(f.type)
          && f.size <= 10 * 1024 * 1024
      );
      if (valid.length < files.length) {
        this.$toast.error("Some files rejected: only JPG, PNG, PDF under 10MB allowed.");
      }
      const mapped = valid.map(f => ({
        file:    f,
        preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      }));
      const current = this.selectedFiles[testId] || [];
      this.selectedFiles[testId] = [...current, ...mapped].slice(0, 5);
      if (current.length + mapped.length > 5) {
        this.$toast.error("Maximum 5 files per test allowed.");
      }
    },

    removeFile(testId, index) {
      const file = this.selectedFiles[testId]?.[index];
      if (file?.preview) URL.revokeObjectURL(file.preview);
      this.selectedFiles[testId].splice(index, 1);
      this.uploadProgress[testId]?.splice(index, 1);
    },

    async uploadLabFilesForTest(testId) {
      const files = this.selectedFiles[testId] || [];
      if (!files.length) return [];

      this.uploadProgress[testId] = new Array(files.length).fill(0);

      const results = await Promise.allSettled(
        files.map(async (fileObj, index) => {
          const file = fileObj.file;
          const s3   = new AWS.S3({
            endpoint:         process.env.VUE_APP_DO_ENDPOINT,
            signatureVersion: "v4",
            accessKeyId:      process.env.VUE_APP_DO_ACCESS_KEY,
            secretAccessKey:  process.env.VUE_APP_DO_SECRET_KEY,
            region:           process.env.VUE_APP_DO_REGION,
          });
          const key    = `lab_result_${testId}_${Date.now()}_${file.name}`;
          const params = {
            Bucket:      "jiji-bucket",
            Key:         key,
            Body:        file,
            ACL:         "public-read",
            ContentType: file.type,
          };
          try {
            const upload = s3.upload(params);
            upload.on("httpUploadProgress", (progress) => {
              const pct = Math.round((progress.loaded / progress.total) * 100);
              this.uploadProgress[testId][index] = pct;
            });
            const result = await upload.promise();
            return { success: true, url: result.Location, filename: file.name };
          } catch (error) {
            console.error(`Upload failed for ${file.name}:`, error);
            return { success: false, url: "", filename: file.name };
          }
        })
      );
      return results.map(r => r.status === "fulfilled" ? r.value : r.reason);
    },

    // ── Validation ─────────────────────────────────────────────────────────────

    // Same shape the backend expects: [{ lab_analyte_id, value }, ...], empty values dropped.
    analyteResultsFor(testId) {
      return Object.entries(this.forms[testId]?.analyte_results || {})
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([lab_analyte_id, value]) => ({ lab_analyte_id, value }));
    },

    validate() {
      const missing = this.pendingTests.filter(t => {
        const hasAnalytes = (this.analytes[t.id] || []).length > 0;
        return hasAnalytes
          ? this.analyteResultsFor(t.id).length === 0
          : !this.forms[t.id]?.results?.trim();
      });
      if (missing.length) {
        this.$toast.error(
          `Please enter results for: ${missing.map(t => t.test_type?.name).join(", ")}`
        );
        return false;
      }
      if (!this.reviewComment.trim()) {
        this.$toast.error("Please enter review comments before publishing.");
        return false;
      }
      return true;
    },

    // ── Main action ────────────────────────────────────────────────────────────

    async captureAndPublish() {
      if (!this.validate()) return;

      this.saving = true;
      try {
        // Step 1: Upload files + capture results for each pending test sequentially
        const capturedIds = [];

        for (const test of this.pendingTests) {
          const form = this.forms[test.id];

          // Upload any attached files for this specific test first
          const uploadResults = await this.uploadLabFilesForTest(test.id);
          const urls = uploadResults
            .filter(r => r.success)
            .map(r => ({ url: r.url, filename: r.filename }));

          if (urls.length < (this.selectedFiles[test.id] || []).length) {
            this.$toast.error(`Some files failed to upload for "${test.test_type?.name}"`);
          }

          const payload = {
            labRequestId:    test.id,
            analyte_results: this.analyteResultsFor(test.id),
            results:         form.results || "", // kept for tests with no linked analytes
            comment:         form.comment || "",
            url:             urls,
            userId:          this.currentUser.UserId,
            facilityId:      this.currentUser.FacilityId,
          };

          const response = await apiClient.captureSampleResult(payload);

          if (!response.success) {
            this.$toast.error(`Failed to capture results for "${test.test_type?.name}"`);
            this.saving = false;
            return;
          }

          capturedIds.push(test.id);
          this.submittedIds.push(test.id);
        }

        // Step 2: Review & publish all captured tests in one call (POST /review/lab/results)
        const reviewPayload = {
          ids:        capturedIds,
          comment:    this.reviewComment,
          facilityId: this.currentUser.FacilityId,
          userId:     this.currentUser.UserId,
        };

        const reviewResponse = await apiClient.reviewLabResults(reviewPayload);

        if (!reviewResponse.success) {
          this.$toast.error(
            "Results were captured but failed to publish. Please use the Lab interface to publish them."
          );
          this.saving = false;
          return;
        }

        this.allPublished = true;
        this.$toast.success("External lab results captured and published successfully.");
        this.$emit("results-published");

      } catch (error) {
        console.error("ExternalLabResultsTab error:", error);
        this.$toast.error("An error occurred: " + error.message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
label {
  pointer-events: auto !important;
  cursor: pointer !important;
}
</style>